import React, { useState, useEffect } from "react";
import api from "../api/axios";
import { toast } from "react-toastify";

function AdminPanel() {
  const [templates, setTemplates] = useState([]);
  const [departments, setDepartments] = useState([]); // ✅ Dynamic departments state
  const [editingId, setEditingId] = useState(null);
  const [llmConfig, setLlmConfig] = useState({});
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  
  // ✅ New Department Modal State
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [newDeptName, setNewDeptName] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "", // Will be set dynamically when departments load
    tags: "",
    model: "",
    temperature: 0.5,
    provider: "",
    type: "",
    tone: "Formal",
    verbosity: "Medium",
    promptType: "Prompt",
    systemPrompt: "",
  });

  // Fetch LLM Models for the dropdown
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const res = await api.get("/llm-models");
        if (res.data) setLlmConfig(res.data);
      } catch (err) {
        console.error("Failed to fetch LLM models");
      }
    };
    fetchModels();
  }, []);

  // ✅ Fetch both Templates and Dynamic Departments
  const fetchData = async () => {
    try {
      const [tempRes, deptRes] = await Promise.all([
        api.get("/predefined-agents"),
        api.get("/admin/departments")
      ]);
      setTemplates(tempRes.data);
      setDepartments(deptRes.data);
      
      // Set default category if creating a new template and none is selected
      if (deptRes.data.length > 0 && !formData.category) {
        setFormData(prev => ({ ...prev, category: deptRes.data[0] }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ✅ Handle Creating a New Department
  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    if (!newDeptName.trim()) return;
    
    try {
      const res = await api.post("/admin/departments", { name: newDeptName });
      toast.success(`Department "${res.data.name}" created!`);
      // Update the UI immediately
      setDepartments(prev => [...prev, res.data.name]);
      setNewDeptName("");
      setShowDeptModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create department");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      let finalTags = formData.tags;
      if (typeof finalTags === "string") {
        finalTags = finalTags
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t !== "");
      }

      const finalTemp = parseFloat(formData.temperature) || 0.5;

      const payload = {
        name: formData.name,
        description: formData.description,
        category: formData.category || departments[0], // Dynamic Fallback
        tags: finalTags,
        model: formData.model,
        temperature: finalTemp,
        provider: formData.provider,
        type: formData.type,
        tone: formData.tone,
        verbosity: formData.verbosity,
        promptType: formData.promptType,
        system_prompt: formData.systemPrompt,
      };

      if (editingId) {
        const res = await api.put(`/admin/update-template/${editingId}`, payload);
        toast.success("Update Successful!");
        setTemplates((prev) =>
          prev.map((t) => (t.id === editingId ? res.data.template : t))
        );
      } else {
        const res = await api.post("/admin/create-template", payload);
        toast.success("New Template Published!");
        setTemplates((prev) => [res.data.template, ...prev]);
      }

      resetForm();
      setShowTemplateModal(false);
    } catch (err) {
      console.error("Action Error:", err.response?.data || err.message);
      toast.error(err.response?.data?.message || "Operation failed.");
    }
  };

  const handleEditClick = (template) => {
    setEditingId(template.id);
    setFormData({
      name: template.name || "",
      description: template.description || "",
      category: template.category || departments[0], // Dynamic Fallback
      tags: Array.isArray(template.tags)
        ? template.tags.join(", ")
        : template.tags || "",
      model: template.model || "",
      temperature: template.temperature ?? 0.5,
      provider: template.provider || "",
      type: template.type || "",
      tone: template.tone || "Formal",
      verbosity: template.verbosity || "Medium",
      promptType: template.prompt_type || "Prompt",
      systemPrompt: template.system_prompt || "",
    });
    setShowTemplateModal(true);
    window.scrollTo(0, 0);
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: "",
      description: "",
      category: departments[0] || "",
      tags: "",
      model: "",
      temperature: 0.5,
      provider: "",
      type: "",
      tone: "Formal",
      verbosity: "Medium",
      promptType: "Prompt",
      systemPrompt: "",
    });
  };

  const handleCreateNew = () => {
    resetForm();
    setShowTemplateModal(true);
  };

  const handleCloseModal = () => {
    resetForm();
    setShowTemplateModal(false);
  };

  const handleDelete = async (id, name) => {
    const confirmMessage = `⚠️ IMPORTANT WARNING ⚠️\n\nDeleting "${name.toUpperCase()}" will PERMANENTLY remove all the agents built under this template.\n\nProceed?`;
    if (window.confirm(confirmMessage)) {
      try {
        await api.delete(`/admin/delete-template/${id}`);
        toast.success("Wiped from database.");
        fetchData();
      } catch (err) {
        toast.error("Delete failed");
      }
    }
  };

  // ✅ ADMIN LOGOUT FUNCTION
  const handleAdminLogout = async () => {
    try {
      await api.post("/admin/internal-logout");
    } catch (err) {
      console.error("Failed to log out on server:", err);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("isInternalAdmin");
      window.location.href = "/login";
    }
  };

  // ✅ Dynamically assign colors based on department names (with a solid fallback for new ones)
  const getCategoryColor = (category) => {
    if (!category) return { bg: "#f0f4ff", color: "#4a6cf7" };
    const cat = category.toLowerCase();
    if (cat.includes("hr")) return { bg: "#eefbf3", color: "#19a95a" };
    if (cat.includes("finance")) return { bg: "#fff8e8", color: "#f59e0b" };
    if (cat.includes("legal")) return { bg: "#f7efff", color: "#b155ff" };
    // Default style for any brand new department created dynamically
    return { bg: "#f0f4ff", color: "#4a6cf7" }; 
  };

  // ✅ Dynamically assign icons based on department names
  const getCategoryIcon = (category) => {
    if (!category) return "/images/internaldoc.svg";
    const cat = category.toLowerCase();
    if (cat.includes("hr")) return "/images/internalhr2.svg";
    if (cat.includes("finance")) return "/images/internalfinance2.svg";
    if (cat.includes("legal")) return "/images/internallegal2.svg";
    // Default icon for any brand new department created dynamically
    return "/images/internaldoc.svg"; 
  };

  const availableModels = formData.provider ? llmConfig[formData.provider] || [] : [];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');

        .admin-panel-wrapper {
          height: 100vh;
          overflow-y: auto;
          background: #f8f9fc;
          font-family: 'DM Sans', sans-serif;
          padding: 32px;
        }

        .admin-header {
          margin-bottom: 32px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
          flex-wrap: wrap;
        }

        .admin-header-left {
          flex: 1;
          min-width: 260px;
        }

        .admin-header-badge {
          display: inline-flex;
          align-items: center;
          background: transparent;
          color: #0f2f8f;
          font-size: 13px;       
          font-weight: 700;      
          letter-spacing: 2.5px; 
          text-transform: uppercase;
          padding: 0;
          margin-bottom: 10px;
        }

        .admin-header h1 {
          font-size: 26px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 6px 0;
          letter-spacing: -0.5px;
        }

        .admin-header p {
          color: #475569;
          font-size: 14px;
          margin: 0;
        }

        /* ✅ Action Buttons Group Layout (Stacks them on the right) */
        .action-buttons-group {
          display: flex;
          flex-direction: column;
          gap: 10px;
          align-items: flex-end;
        }

        .top-create-btn.image-btn {
          padding: 0;
          background: transparent;
          border: none;
          box-shadow: none;
        }

        .top-create-btn.image-btn img {
          display: block;
          height: 38px;  
          width: auto;
        }

        .top-create-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 18px;
          background: #0f2f8f;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
          width: 100%;
        }

        .top-create-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(15, 47, 143, 0.22);
        }

        /* ✅ Dynamic Stats Row */
        .admin-stats-row {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 32px;
        }

        .admin-stat-card {
          flex: 1 1 calc(25% - 20px);
          min-width: 200px;
          background: white;
          border: 1px solid #e8edf5;
          border-radius: 14px;
          padding: 26px 26px 24px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 18px;
          min-height: 128px;
          box-sizing: border-box;
        }

        .admin-stat-icon {
          width: auto;
          height: auto;
          border-radius: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: transparent !important;
        }

        .admin-stat-info p {
          margin: 0;
          font-size: 11px;
          color: #94a3b8;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          line-height: 1.5;
        }

        .admin-stat-info h3 {
          margin: 8px 0 0 0;
          font-size: 22px;
          font-weight: 700;
          color: #0f172a;
          line-height: 1;
        }

        .admin-table-card {
          background: white;
          border: 1px solid #e7ecf4;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(15, 23, 42, 0.03);
          width: 100%;
        }

        .admin-table-header {
          padding: 22px 24px;
          border-bottom: 1px solid #edf2f7;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #ffffff;
        }

        .admin-table-header h5 {
          margin: 0;
          font-size: 14px;
          font-weight: 700;
          color: #334155;
        }

        .admin-table-header span {
          font-size: 11px;
          color: #94a3b8;
          background: #f3f6fb;
          padding: 6px 12px;
          border-radius: 999px;
          font-weight: 500;
        }

        .template-table {
          width: 100%;
          border-collapse: collapse;
        }

        .template-table thead tr {
          background: #f9fbfd;
        }

        .template-table thead th {
          padding: 12px 26px;
          font-size: 11px;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 1.4px;
          text-align: left;
          border-bottom: 1px solid #edf2f7;
        }

        .template-table thead th:last-child {
          text-align: center;
          width: 120px;
        }

        .template-table tbody tr {
          border-bottom: 1px solid #f1f5f9;
          transition: background 0.15s ease;
        }

        .template-table tbody tr:hover {
          background: #fcfdff;
        }

        .template-table tbody tr.editing {
          background: #f7fbff;
        }

        .template-table tbody tr:last-child {
          border-bottom: none;
        }

        .template-table td {
          padding: 18px 26px;
          vertical-align: middle;
        }

        .template-name-cell {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .template-icon {
          width: 24px;
          min-width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          background: transparent !important;
        }
          .template-icon img {
          width: 15px;
          height: 15px;
          object-fit: contain;
          display: block;
        }
        .icon-action-btn img {
          width: 12px;
          height: 12px;
          object-fit: contain;
          display: block;
        }
        .template-name {
          font-size: 14px;
          font-weight: 500;
          color: #1e293b;
          line-height: 1.4;
        }

        .category-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 5px 11px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
          border: none;
          min-height: 28px;
        }

        .actions-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
        }

        .icon-action-btn {
          border: none;
          background: transparent;
          padding: 0;
          margin: 0;
          font-size: 15px;
          line-height: 1;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.15s ease, opacity 0.15s ease;
        }

        .icon-action-btn:hover {
          transform: scale(1.08);
          opacity: 0.9;
        }

        .icon-action-btn.edit {
          color: #2563eb;
        }

        .icon-action-btn.delete {
          color: #ff6b6b;
        }

        .empty-state {
          padding: 48px 20px;
          text-align: center;
          color: #94a3b8;
        }

        .empty-state div {
          font-size: 36px;
          margin-bottom: 10px;
        }

        .empty-state p {
          font-size: 13px;
          margin: 0;
        }

        /* MODALS */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.18);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
          z-index: 9999;
        }

        .modal-shell {
          width: 100%;
          max-width: 620px;
          max-height: 95vh;
          border-radius: 18px;
          overflow: hidden;
          background: #ffffff;
          box-shadow: 0 18px 45px rgba(15, 23, 42, 0.14);
          animation: modalPop 0.18s ease-out;
          border: 1px solid #e9eef5;
        }

        .modal-shell.small {
          max-width: 400px;
        }

        @keyframes modalPop {
          from {
            opacity: 0;
            transform: translateY(8px) scale(0.985);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .admin-form-card {
          background: #ffffff;
          border: none;
          border-radius: 0;
          box-shadow: none;
          overflow: hidden;
        }

        .admin-form-header {
          background:  #f4f6f9;
          border-bottom: 1px solid #edf2f7;
          padding: 18px 22px 14px;
          position: relative;
        }

        .admin-form-title-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
        }

        .admin-form-title-wrap {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }

        .admin-form-title-icon {
          color: #0f4aa1;
          font-size: 20px;
          line-height: 1;
          margin-top: 2px;
        }

        .admin-form-header h5 {
          margin: 0;
          font-size: 18px;
          font-weight: 700;
          color: #111827;
          line-height: 1.2;
        }

        .admin-form-header p {
          margin: 5px 0 0 0;
          font-size: 14px;
          color: #6b7280;
          line-height: 1.4;
        }

        .editing-indicator {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 11px;
          color: #1D5DA8;
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          padding: 3px 8px;
          border-radius: 999px;
          margin-left: 8px;
          font-weight: 600;
        }

        .modal-close-btn {
          background: transparent;
          border: none;
          color: #8b9099;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 22px;
          line-height: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          padding: 0;
        }

        .modal-close-btn:hover {
          background: #f3f4f6;
          color: #4b5563;
        }

        .admin-form-body {
          padding: 8px 22px 22px;
          max-height: calc(95vh - 92px);
          overflow-y: auto;
          background: #ffffff;
        }

        .admin-form-body::-webkit-scrollbar {
          width: 6px;
        }

        .admin-form-body::-webkit-scrollbar-track {
          background: #f4f6f9;
        }

        .admin-form-body::-webkit-scrollbar-thumb {
          background: #d3dbe6;
          border-radius: 999px;
        }

        .section-divider {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 800;
          color: #0f4aa1;
          text-transform: uppercase;
          letter-spacing: 1.8px;
          margin: 0 0 14px 0;
          padding-top: 16px;
          border: none;
        }

        .section-divider::before {
          content: "•";
          font-size: 18px;
          line-height: 1;
          color: #0f4aa1;
        }

        .form-group {
          margin-bottom: 12px;
        }

        .form-group label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #4b5563;
          margin-bottom: 7px;
          text-transform: none;
          letter-spacing: 0;
        }

        .form-group input:not(.temp-slider),
        .form-group textarea,
        .form-group select {
          width: 100%;
          height: 40px;
          padding: 0 14px;
          border: 1px solid #e5eaf1;
          border-radius: 4px;
          font-size: 13px;
          font-family: 'DM Sans', sans-serif;
          color: #111827;
          background: #f7f9fc;
          outline: none;
          box-sizing: border-box;
          transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
        }

        .form-group textarea {
          min-height: 40px;
          height: 40px;
          resize: vertical;
          padding: 12px 14px;
        }

        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: #9ca3af;
        }

        .form-group input:focus,
        .form-group textarea:focus,
        .form-group select:focus {
          border-color: #bfd1ea;
          background: #ffffff;
          box-shadow: 0 0 0 2px rgba(15, 74, 161, 0.05);
        }

        .form-group select:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

      .temp-label-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;
      }

      .temp-title {
        font-size: 13px;
        font-weight: 600;
        color: #4b5563;
      }

      .temp-value {
        font-size: 11px;
        font-weight: 700;
        color: #0f4aa1;
        background: #eef4ff;
        padding: 2px 6px;
        border-radius: 2px;
        min-width: 28px;
        text-align: center;
      }

      .temp-slider-wrap {
        display: flex;
        align-items: center;
        padding: 0;
        background: transparent;
        border: none;
        border-radius: 0;
        height: auto;
        box-sizing: border-box;
      }

      .temp-slider {
        width: 100%;
        height: 4px;
        outline: none;
        cursor: pointer;
        border: none !important;
        padding: 0 !important;
        box-shadow: none !important;
        -webkit-appearance: none;
        appearance: none;
        background: transparent;
      }

      .temp-slider::-webkit-slider-runnable-track {
        height: 4px;
        background: #d9dee5;
        border-radius: 999px;
      }

      .temp-slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: #0f4aa1;
        border: none;
        margin-top: -7px;
        box-shadow: none;
      }

      .temp-slider::-moz-range-track {
        height: 4px;
        background: #d9dee5;
        border-radius: 999px;
        border: none;
      }

      .temp-slider::-moz-range-thumb {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: #0f4aa1;
        border: none;
        box-shadow: none;
      }

        .form-actions {
          display: block;
          gap: 0;
          margin-top: 14px;
          padding-top: 4px;
          border-top: none;
        }

        .btn-publish {
          width: 100%;
          height: 44px;
          padding: 0 18px;
          background: #0c2d8b;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 15px;
          font-weight: 700;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background 0.2s ease, transform 0.15s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: none;
        }

        .btn-publish:hover {
          background: #0a2677;
          transform: translateY(-1px);
        }

        .btn-publish.update {
          background: #0c2d8b;
        }

        .btn-cancel {
          display: none;
        }

        @media (max-width: 768px) {
          .admin-panel-wrapper { padding: 20px; }
          .admin-header { flex-direction: column; align-items: stretch; }
          .action-buttons-group { align-items: stretch; }
          .top-create-btn { width: 100%; }
          .form-row { grid-template-columns: 1fr; }
          .template-table { min-width: 700px; }
          .admin-table-card { overflow-x: auto; }
          .modal-shell { max-height: 96vh; }
        }
      `}</style>

      <div className="admin-panel-wrapper">
        <div className="admin-header">
          <div className="admin-header-left">
            <div className="admin-header-badge">Kinexar Internal</div>
            <h1>Template Control Center</h1>
            <p>Create, manage and publish predefined agent templates for all clients</p>
          </div>

          <div className="action-buttons-group">
            <button className="top-create-btn image-btn" onClick={handleCreateNew} aria-label="Create New Template">
              <img src="/images/internalcreatebutton.svg" alt="Create New Template" />
            </button>
            {/* ✅ NEW BUTTON: Create Department */}
            <button className="top-create-btn" onClick={() => setShowDeptModal(true)}>
              + Add New Department
            </button>
          </div>
        </div>

        {/* ✅ DYNAMIC STATS ROW */}
        <div className="admin-stats-row">
          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ background: "#eff6ff" }}>
              <img src="/images/internaldoc.svg" alt="Total Templates" />
            </div>
            <div className="admin-stat-info">
              <p>Total Templates</p>
              <h3>{templates.length}</h3>
            </div>
          </div>

          {/* Loop over dynamically fetched departments */}
          {departments.map((dept, idx) => {
            const count = templates.filter((t) => t.category === dept).length;
            const colors = getCategoryColor(dept);
            return (
              <div key={idx} className="admin-stat-card">
                <div className="admin-stat-icon" style={{ background: colors.bg }}>
                  <img src={getCategoryIcon(dept)} alt={dept} />
                </div>
                <div className="admin-stat-info">
                  <p>{dept}</p>
                  <h3>{count}</h3>
                </div>
              </div>
            );
          })}
        </div>

        <div className="admin-table-card">
          <div className="admin-table-header">
            <h5>All Predefined Templates</h5>
            <span>{templates.length} templates</span>
          </div>

          {templates.length === 0 ? (
            <div className="empty-state">
              <div>📭</div>
              <p>No templates yet. Create your first one!</p>
            </div>
          ) : (
            <table className="template-table">
              <thead>
                <tr>
                  <th>Template</th>
                  <th>Category</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => {
                  const colors = getCategoryColor(t.category);
                  return (
                    <tr key={t.id} className={editingId === t.id ? "editing" : ""}>
                      <td>
                        <div className="template-name-cell">
                          <div className="template-icon">
                            <img src={getCategoryIcon(t.category)} alt={t.category || "Template"} />
                          </div>
                          <span className="template-name">{t.name}</span>
                        </div>
                      </td>

                      <td>
                        <span
                          className="category-badge"
                          style={{
                            background: colors.bg,
                            color: colors.color,
                          }}
                        >
                          {t.category}
                        </span>
                      </td>

                      <td>
                        <div className="actions-cell">
                          <button
                            className="icon-action-btn edit"
                            onClick={() => handleEditClick(t)}
                            title="Edit"
                            aria-label="Edit"
                          >
                            <img src="/images/internalpen.svg" alt="Edit" />
                          </button>

                          <button
                            className="icon-action-btn delete"
                            onClick={() => handleDelete(t.id, t.name)}
                            title="Delete"
                            aria-label="Delete"
                          >
                            <img src="/images/internalbin.svg" alt="Delete" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* LOGOUT BUTTON */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
          <button 
            className="top-create-btn" 
            onClick={handleAdminLogout}
            style={{ 
              background: '#0f2f8f', 
              padding: '12px 24px',
              fontSize: '15px'
            }}
          >
            <span style={{ fontSize: '18px', marginRight: '6px', fontWeight: 'bold' }}>⏏</span> 
            Secure System Logout
          </button>
        </div>

      </div>

      {/* ✅ MODAL 1: CREATE / EDIT TEMPLATE (Old Form completely preserved) */}
      {showTemplateModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-shell" onClick={(e) => e.stopPropagation()}>
            <div className="admin-form-card">
              <div className="admin-form-header">
                <div className="admin-form-title-row">
                  <div className="admin-form-title-wrap">
                    <div className="admin-form-title-icon">⊕</div>
                    <div>
                      <h5>
                        {editingId ? "Edit Template" : "Create New Template"}
                        {editingId && <span className="editing-indicator">Editing Mode</span>}
                      </h5>
                      <p>
                        {editingId
                          ? "Configure foundational parameters for updating this AI agent."
                          : "Configure foundational parameters for new AI agents."}
                      </p>
                    </div>
                  </div>

                  <button className="modal-close-btn" onClick={handleCloseModal}>
                    ×
                  </button>
                </div>
              </div>

              <div className="admin-form-body">
                <form onSubmit={handleSave}>
                  <div className="section-divider">Basic Information</div>

                  <div className="form-group">
                    <label>Template Name</label>
                    <input
                      type="text"
                      placeholder="e.g., Senior Legal Analyst"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      placeholder="Define the primary purpose of this agent..."
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) =>
                          setFormData({ ...formData, category: e.target.value })
                        }
                      >
                        {/* ✅ Dynamically map departments */}
                        {departments.map((dept, idx) => (
                          <option key={idx} value={dept}>{dept}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Type</label>
                      <select
                        value={formData.type}
                        onChange={(e) =>
                          setFormData({ ...formData, type: e.target.value })
                        }
                      >
                        <option value="" disabled hidden>
                          Select Type
                        </option>
                        <option value="HR Policy Agent">HR Policy Agent</option>
                        <option value="HR Performance & Training Agent">
                          HR Performance & Training Agent
                        </option>
                        <option value="Financial Audit & Risk Assistant">
                          Financial Audit & Risk Assistant
                        </option>
                        <option value="RFP Creator Agent">RFP Creator Agent</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Tags(Comma Seperated)</label>
                    <input
                      type="text"
                      placeholder="e.g., Policy, HR, Global"
                      value={formData.tags}
                      onChange={(e) =>
                        setFormData({ ...formData, tags: e.target.value })
                      }
                    />
                  </div>

                  <div className="section-divider">LLM Configuration</div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Provider</label>
                      <select
                        value={formData.provider}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            provider: e.target.value,
                            model: "",
                          })
                        }
                      >
                        <option value="" disabled hidden>
                          Select Provider
                        </option>
                        {Object.keys(llmConfig || {}).map((provider) => (
                          <option key={provider} value={provider}>
                            {provider}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Model</label>
                      <select
                        value={formData.model}
                        onChange={(e) =>
                          setFormData({ ...formData, model: e.target.value })
                        }
                        disabled={!formData.provider}
                      >
                        <option value="">Select Model</option>
                        {availableModels.map((model) => (
                          <option key={model} value={model}>
                            {model}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="temp-label-row">
                      <span className="temp-title">Temperature</span>
                      <span className="temp-value">{formData.temperature}</span>
                    </div>

                    <div className="temp-slider-wrap">
                      <input
                        type="range"
                        className="temp-slider"
                        min="0"
                        max="1"
                        step="0.1"
                        value={formData.temperature}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            temperature: parseFloat(e.target.value),
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Tone</label>
                      <select
                        value={formData.tone}
                        onChange={(e) =>
                          setFormData({ ...formData, tone: e.target.value })
                        }
                      >
                        <option value="Formal">Professional</option>
                        <option value="Neutral">Neutral</option>
                        <option value="Friendly">Friendly</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Verbosity</label>
                      <select
                        value={formData.verbosity}
                        onChange={(e) =>
                          setFormData({ ...formData, verbosity: e.target.value })
                        }
                      >
                        <option value="Low">Concise</option>
                        <option value="Medium">Medium</option>
                        <option value="High">Detailed</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Prompt Type</label>
                    <select
                      value={formData.promptType}
                      onChange={(e) =>
                        setFormData({ ...formData, promptType: e.target.value })
                      }
                    >
                      <option value="Prompt">Enter system instructions...</option>
                      <option value="Custom Prompt">Custom Prompt</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>System Prompt</label>
                    <textarea
                      rows="4"
                      placeholder="Enter system instructions..."
                      value={formData.systemPrompt}
                      onChange={(e) =>
                        setFormData({ ...formData, systemPrompt: e.target.value })
                      }
                      style={{ minHeight: "38px" }}
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className={`btn-publish ${editingId ? "update" : ""}`}
                    >
                      {editingId ? "Save Changes" : "Initialize Template"}
                    </button>

                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={handleCloseModal}
                    >
                      Close
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ MODAL 2: ADD NEW DEPARTMENT */}
      {showDeptModal && (
        <div className="modal-overlay" onClick={() => setShowDeptModal(false)}>
          <div className="modal-shell small" onClick={(e) => e.stopPropagation()}>
            <div className="admin-form-header">
              <div className="admin-form-title-row">
                <div>
                  <h5>Add New Department</h5>
                  <p>Create a new category for templates.</p>
                </div>
                <button className="modal-close-btn" onClick={() => setShowDeptModal(false)}>×</button>
              </div>
            </div>

            <div className="admin-form-body">
              <form onSubmit={handleCreateDepartment}>
                <div className="form-group">
                  <label>Department Name</label>
                  <input
                    type="text"
                    placeholder="e.g. IT Agents"
                    value={newDeptName}
                    onChange={(e) => setNewDeptName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-publish">
                    Create Department
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminPanel;